import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import CredoraToastContent from "./CredoraToastContent";

const DEFAULT_OPTIONS = {
  position: "top-right",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

/**
 * @param {unknown} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function formatApiError(err, fallback = "Something went wrong.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
  }
  if (typeof err?.message === "string" && err.message) {
    return err.message;
  }
  return fallback;
}

function renderToast(type, message, options = {}) {
  const { className: extraClass, ...rest } = options;
  return toast(
    <CredoraToastContent type={type} message={message} />,
    {
      ...DEFAULT_OPTIONS,
      ...rest,
      type,
      icon: false,
      className: ["credora-toast", `credora-toast--${type}`, extraClass].filter(Boolean).join(" "),
    }
  );
}

function resolveMessage(input, fallback) {
  if (typeof input === "string") {
    return input;
  }
  return formatApiError(input, fallback);
}

export const credoraToast = {
  success(message, options) {
    return renderToast("success", message, options);
  },
  error(input, fallback = "Something went wrong.", options) {
    return renderToast("error", resolveMessage(input, fallback), options);
  },
  warning(message, options) {
    return renderToast("warning", message, options);
  },
  info(message, options) {
    return renderToast("info", message, options);
  },
  created(entity, options) {
    return this.success(`${entity} created successfully.`, options);
  },
  updated(entity, options) {
    return this.success(`${entity} updated successfully.`, options);
  },
  deleted(entity, options) {
    return this.success(`${entity} deleted successfully.`, options);
  },
  saved(entity, options) {
    return this.success(`${entity} saved successfully.`, options);
  },
  refreshed(label = "Data", options) {
    const text = label ? `${label} refreshed.` : "Data refreshed.";
    return this.info(text, options);
  },
};

/**
 * Run an async CRUD action with global success/error toasts.
 * @template T
 * @param {() => Promise<T>} action
 * @param {{ successMessage?: string, errorMessage?: string, showSuccess?: boolean }} [options]
 * @returns {Promise<T>}
 */
export async function runCrud(action, options = {}) {
  const { successMessage, errorMessage = "Operation failed.", showSuccess = true } = options;
  try {
    const result = await action();
    if (showSuccess && successMessage) {
      credoraToast.success(successMessage);
    }
    return result;
  } catch (err) {
    credoraToast.error(err, errorMessage);
    throw err;
  }
}

/**
 * User-initiated refresh — shows info toast on success, error toast on failure.
 * @template T
 * @param {() => Promise<T>} action
 * @param {{ label?: string, errorMessage?: string }} [options]
 */
export async function runRefresh(action, options = {}) {
  const { label = "Data", errorMessage = "Failed to refresh." } = options;
  try {
    const result = await action();
    credoraToast.refreshed(label);
    return result;
  } catch (err) {
    credoraToast.error(err, errorMessage);
    throw err;
  }
}

/**
 * Initial or silent load — error toast only (no success toast on mount).
 * @template T
 * @param {() => Promise<T>} action
 * @param {{ errorMessage?: string }} [options]
 */
export async function runLoad(action, options = {}) {
  const { errorMessage = "Failed to load." } = options;
  try {
    return await action();
  } catch (err) {
    credoraToast.error(err, errorMessage);
    throw err;
  }
}

/**
 * For dashboards that reload via reloadKey + useEffect.
 * Call requestReload on Refresh/Retry click; call completeReload in fetch then/catch.
 */
export function useCredoraReloadFeedback(label = "Data") {
  const pendingRef = useRef(false);
  const [reloadKey, setReloadKey] = useState(0);

  const requestReload = useCallback(() => {
    pendingRef.current = true;
    setReloadKey((k) => k + 1);
  }, []);

  const completeReload = useCallback(
    (err) => {
      if (!pendingRef.current) {
        return;
      }
      pendingRef.current = false;
      if (err) {
        credoraToast.error(err, `Failed to refresh ${label.toLowerCase()}.`);
      } else {
        credoraToast.refreshed(label);
      }
    },
    [label]
  );

  return { reloadKey, requestReload, completeReload };
}

export default credoraToast;
